/**
* Klaviyo star rating widget wrapper
* Observes and formats Klaviyo review widget output to display custom review counts
*/

export const KLAVIYO_STAR_RATING_READY_EVENT = 'klaviyo-star-rating:ready'

declare global {
  interface Window {
    klReviewsObject?: { renderWidgets: () => void }
  }
}

// Klaviyo's onsite script only scans the DOM for `.klaviyo-star-rating-widget`
// elements present when it boots, so it never picks up ones Preact mounts later
// (collection/search grids, recommendation carousels). `renderWidgets()` is the
// function Klaviyo's own bundle exposes to re-trigger that scan — debounce it
// across a burst of cards mounting together, and retry with backoff in case
// Klaviyo's embed script hasn't finished loading yet.
const RENDER_WIDGETS_RETRY_DELAYS_MS = [100, 300, 700, 1500, 3000]
let renderWidgetsTimeout: NodeJS.Timeout | null = null

function triggerRenderWidgets(attempt = 0) {
  if (window.klReviewsObject?.renderWidgets) {
    window.klReviewsObject.renderWidgets()
    return
  }
  if (attempt >= RENDER_WIDGETS_RETRY_DELAYS_MS.length) return
  setTimeout(() => triggerRenderWidgets(attempt + 1), RENDER_WIDGETS_RETRY_DELAYS_MS[attempt])
}

function scheduleRenderWidgets() {
  if (renderWidgetsTimeout) clearTimeout(renderWidgetsTimeout)
  renderWidgetsTimeout = setTimeout(() => triggerRenderWidgets(), 50)
}

export class KlaviyoStarRatingElement extends HTMLElement {
  private observer: MutationObserver | null = null
  private reviewCountSpan: HTMLSpanElement | null = null
  private hasNotifiedReady = false

  connectedCallback() {
    this.render()
    this.observeKlaviyoWidget()
    scheduleRenderWidgets()
  }

  disconnectedCallback() {
    this.cleanup()
    this.hasNotifiedReady = false
  }

  private render() {
    const productId = this.getAttribute('product-id') || ''
    const productTitle = this.getAttribute('product-title') || ''
    const productType = this.getAttribute('product-type') || ''

    this.innerHTML = `
      <div class="klaviyo-star-rating-container flex flex-nowrap items-center lg:justify-end gap-x-0.25">
        <div
          class="klaviyo-star-rating-widget flex"
          slot="productReviews"
          data-id="${productId}"
          data-product-title="${productTitle}"
          data-product-type="${productType}"
        ></div>
        <span class="custom-star-rating paragraph-xs text-type-black-primary"></span>
      </div>
    `

    this.reviewCountSpan = this.querySelector('.custom-star-rating')
  }

  private observeKlaviyoWidget() {
    const targetNode = this.querySelector('.klaviyo-star-rating-widget')
    if (!targetNode) return

    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          const widget = targetNode.querySelector('.kl_reviews__star_rating_widget')
          if (widget) {
            this.notifyReady()
          }

          const reviewLabel = targetNode.querySelector('.kl_reviews__star_rating_widget__label')
          if (reviewLabel && this.reviewCountSpan) {
            const originalText = reviewLabel.textContent?.trim() || ''
            const count = originalText.replace(/\breviews?\b/gi, '').trim()
            this.reviewCountSpan.textContent = `(${count})`
            this.cleanup()
          }
        }
      })
    })

    this.observer.observe(targetNode, { childList: true, subtree: true })
  }

  private notifyReady() {
    if (this.hasNotifiedReady) return
    this.hasNotifiedReady = true
    this.dispatchEvent(
      new CustomEvent(KLAVIYO_STAR_RATING_READY_EVENT, { bubbles: true })
    )
  }

  private cleanup() {
    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    }
  }
}
